import { useState, useEffect } from "react";
import { authAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { formatFecha } from "../../utils";
import CampoPassword from "../../components/admin/CampoPassword";
import { passwordValida, MENSAJE_REGLAS } from "../../constants/password";
import toast from "react-hot-toast";
import "./Perfil.css";

const PASS_VACIO = { actual: "", nueva: "", confirmar: "" };

// Texto amigable para cada acción del historial
const ETIQUETAS_ACCION = {
  login: "🔑 Login",
  login_2fa: "🔐 Login con 2FA",
  password_change: "🔒 Cambio de contraseña",
  password_reset_admin: "🛠️ Contraseña cambiada por el admin",
};

export default function Perfil() {
  const { usuario } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [qr, setQr] = useState(null);
  const [codigoActivar, setCodigoActivar] = useState("");
  const [codigoDesactivar, setCodigoDesactivar] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [paso2fa, setPaso2fa] = useState("idle"); // idle | setup | confirming | done
  const [cargando, setCargando] = useState(true);
  const [pass, setPass] = useState(PASS_VACIO);
  const [guardandoPass, setGuardandoPass] = useState(false);

  useEffect(() => {
    Promise.all([
      authAPI.me(),
      authAPI.accessLog(),
    ]).then(([meRes, logRes]) => {
      setPerfil(meRes.data.data);
      setHistorial(logRes.data.data);
    }).catch(() => toast.error("Error cargando perfil"))
        .finally(() => setCargando(false));
  }, []);

  const iniciarSetup2fa = async () => {
    try {
      const res = await authAPI.setup2fa();
      setQr(res.data.data.qr);
      setPaso2fa("setup");
    } catch { toast.error("Error generando QR"); }
  };

  const confirmarActivar2fa = async () => {
    if (!codigoActivar || codigoActivar.length !== 6) { toast.error("Ingresa el código de 6 dígitos"); return; }
    try {
      const res = await authAPI.enable2fa({ code: codigoActivar });
      setBackupCodes(res.data.data.backupCodes);
      setPerfil((p) => ({ ...p, totp_enabled: true }));
      setPaso2fa("done");
      toast.success("✅ Autenticación de dos factores activada");
    } catch (err) { toast.error(err.response?.data?.message || "Código incorrecto"); }
  };

  const desactivar2fa = async () => {
    if (!codigoDesactivar || codigoDesactivar.length !== 6) { toast.error("Ingresa el código de 6 dígitos"); return; }
    try {
      await authAPI.disable2fa({ code: codigoDesactivar });
      setPerfil((p) => ({ ...p, totp_enabled: false }));
      setCodigoDesactivar("");
      toast.success("2FA desactivado");
    } catch (err) { toast.error(err.response?.data?.message || "Código incorrecto"); }
  };

  const cambiarPassword = async (e) => {
    e.preventDefault();
    if (!pass.actual) { toast.error("Escribe tu contraseña actual"); return; }
    if (!passwordValida(pass.nueva)) { toast.error(MENSAJE_REGLAS); return; }
    if (pass.nueva !== pass.confirmar) { toast.error("La confirmación no coincide con la nueva contraseña"); return; }
    if (pass.nueva === pass.actual) { toast.error("La nueva contraseña debe ser distinta a la actual"); return; }

    setGuardandoPass(true);
    try {
      await authAPI.cambiarPassword({ actual: pass.actual, nueva: pass.nueva });
      setPass(PASS_VACIO);
      toast.success("✅ Tu contraseña se actualizó");
      authAPI.accessLog().then((r) => setHistorial(r.data.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || "No se pudo cambiar la contraseña");
    } finally {
      setGuardandoPass(false);
    }
  };

  const confirmacionNoCoincide = pass.confirmar.length > 0 && pass.confirmar !== pass.nueva;

  if (cargando) return <div className="spinner" />;

  return (
      <div className="perfil-page fade-in">
        <div className="page-header">
          <h1 className="heading-serif page-title">Mi Perfil y Seguridad</h1>
        </div>

        <div className="perfil-grid">
          {/* Info del perfil */}
          <section className="card perfil-card">
            <h2 className="section-heading" style={{ marginBottom: "1.5rem" }}>Información de la cuenta</h2>
            <div className="perfil-avatar-row">
              <div className="perfil-big-avatar">{perfil?.name?.charAt(0).toUpperCase()}</div>
              <div>
                <p className="perfil-name">{perfil?.name}</p>
                <p className="perfil-email">{perfil?.email}</p>
                <p className="perfil-since">Cuenta desde: {formatFecha(perfil?.created_at)}</p>
              </div>
            </div>
            {perfil?.last_login && (
                <p style={{ fontSize: ".85rem", color: "var(--color-text-muted)", marginTop: "1rem" }}>
                  🕐 Último acceso: {new Date(perfil.last_login).toLocaleString("es-MX")}
                </p>
            )}
          </section>

          {/* Seguridad 2FA */}
          <section className="card perfil-card">
            <h2 className="section-heading" style={{ marginBottom: ".5rem" }}>Autenticación de dos factores</h2>
            <p style={{ fontSize: ".875rem", color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
              Agrega una capa extra de seguridad con Google Authenticator.
            </p>

            <div className="twofa-status">
            <span className={`twofa-badge ${perfil?.totp_enabled ? "on" : "off"}`}>
              {perfil?.totp_enabled ? "🔐 ACTIVADO" : "🔓 DESACTIVADO"}
            </span>
            </div>

            {/* 2FA desactivado — flujo de activación */}
            {!perfil?.totp_enabled && paso2fa === "idle" && (
                <button className="btn btn-primary" style={{ marginTop: "1.25rem" }} onClick={iniciarSetup2fa}>
                  Activar 2FA con Google Authenticator
                </button>
            )}

            {!perfil?.totp_enabled && paso2fa === "setup" && qr && (
                <div className="twofa-setup">
                  <p className="twofa-step">Paso 1: Escanea este código QR con Google Authenticator</p>
                  <img src={qr} alt="QR 2FA" className="twofa-qr" />
                  <p className="twofa-step">Paso 2: Ingresa el código de 6 dígitos que aparece en la app</p>
                  <div style={{ display: "flex", gap: ".75rem", alignItems: "center" }}>
                    <input className="form-input" style={{ width: "150px", letterSpacing: ".2em", textAlign: "center", fontSize: "1.1rem" }}
                           maxLength={6} value={codigoActivar} onChange={(e) => setCodigoActivar(e.target.value.replace(/\D/g, ""))}
                           placeholder="000000" />
                    <button className="btn btn-accent" onClick={confirmarActivar2fa}>Confirmar y activar</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setPaso2fa("idle"); setQr(null); }}>Cancelar</button>
                  </div>
                </div>
            )}

            {!perfil?.totp_enabled && paso2fa === "done" && backupCodes && (
                <div className="backup-codes-box">
                  <h3>🔑 Códigos de respaldo</h3>
                  <p>Guarda estos códigos en un lugar seguro. Puedes usarlos si pierdes acceso a tu teléfono. <strong>Cada código solo funciona una vez.</strong></p>
                  <div className="backup-codes-grid">
                    {backupCodes.map((code, i) => (
                        <span key={i} className="backup-code">{code}</span>
                    ))}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => {
                    navigator.clipboard.writeText(backupCodes.join("\n"));
                    toast.success("Códigos copiados al portapapeles");
                  }}>
                    📋 Copiar todos los códigos
                  </button>
                </div>
            )}

            {/* 2FA activado — opción de desactivar */}
            {perfil?.totp_enabled && (
                <div className="twofa-disable">
                  <p style={{ fontSize: ".875rem", color: "var(--color-text-muted)", marginBottom: "1rem" }}>
                    Para desactivar el 2FA, ingresa el código actual de Google Authenticator:
                  </p>
                  <div style={{ display: "flex", gap: ".75rem", alignItems: "center" }}>
                    <input className="form-input" style={{ width: "150px", letterSpacing: ".2em", textAlign: "center", fontSize: "1.1rem" }}
                           maxLength={6} value={codigoDesactivar} onChange={(e) => setCodigoDesactivar(e.target.value.replace(/\D/g, ""))}
                           placeholder="000000" />
                    <button className="btn btn-danger btn-sm" onClick={desactivar2fa}>Desactivar 2FA</button>
                  </div>
                </div>
            )}
          </section>

          {/* Cambiar mi contraseña (cualquier usuario) */}
          <section className="card perfil-card" style={{ gridColumn: "1 / -1" }}>
            <h2 className="section-heading" style={{ marginBottom: ".5rem" }}>Cambiar contraseña</h2>
            <p style={{ fontSize: ".875rem", color: "var(--color-text-muted)", marginBottom: "1.25rem" }}>
              Por seguridad, primero escribe tu contraseña actual.
            </p>
            <form onSubmit={cambiarPassword} className="perfil-pass-form">
              <div className="form-group">
                <label className="form-label" htmlFor="pass-actual">Contraseña actual</label>
                <CampoPassword
                    id="pass-actual"
                    value={pass.actual}
                    onChange={(e) => setPass({ ...pass, actual: e.target.value })}
                    autoComplete="current-password"
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pass-nueva">Nueva contraseña</label>
                <CampoPassword
                    id="pass-nueva"
                    value={pass.nueva}
                    onChange={(e) => setPass({ ...pass, nueva: e.target.value })}
                    autoComplete="new-password"
                    mostrarReglas
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pass-confirmar">Confirmar nueva contraseña</label>
                <CampoPassword
                    id="pass-confirmar"
                    value={pass.confirmar}
                    onChange={(e) => setPass({ ...pass, confirmar: e.target.value })}
                    autoComplete="new-password"
                    invalido={confirmacionNoCoincide}
                />
                {confirmacionNoCoincide && (
                    <span className="form-hint" style={{ color: "var(--color-error, #b91c1c)" }} role="alert">
                  No coincide con la nueva contraseña
                </span>
                )}
              </div>
              <div>
                <button type="submit" className="btn btn-primary" disabled={guardandoPass}>
                  {guardandoPass ? "Guardando..." : "🔒 Actualizar contraseña"}
                </button>
              </div>
            </form>
          </section>

          {/* Historial de accesos */}
          <section className="card perfil-card" style={{ gridColumn: "1 / -1" }}>
            <h2 className="section-heading" style={{ marginBottom: "1.25rem" }}>Historial de accesos recientes</h2>
            {historial.length === 0 ? (
                <p style={{ color: "var(--color-text-muted)", fontSize: ".9rem" }}>Sin registros de acceso</p>
            ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                    <tr><th>Acción</th><th>IP</th><th>Navegador</th><th>Fecha y hora</th></tr>
                    </thead>
                    <tbody>
                    {historial.map((log, i) => (
                        <tr key={i}>
                          <td>
                        <span style={{ fontWeight: 600 }}>
                          {ETIQUETAS_ACCION[log.action] || log.action}
                        </span>
                          </td>
                          <td style={{ fontFamily: "monospace", fontSize: ".85rem" }}>{log.ip_address || "—"}</td>
                          <td style={{ fontSize: ".8rem", color: "var(--color-text-muted)", maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {log.user_agent || "—"}
                          </td>
                          <td style={{ fontSize: ".85rem", color: "var(--color-text-muted)" }}>
                            {new Date(log.created_at).toLocaleString("es-MX")}
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
            )}
          </section>
        </div>
      </div>
  );
}